import React, { useState } from 'react';
import {
  View, Text, StyleSheet, TextInput, TouchableOpacity,
  KeyboardAvoidingView, Platform, FlatList, ActivityIndicator,
} from 'react-native';
import Modal from './Modal';
import { SafeAreaView } from 'react-native-safe-area-context';
import Icon from 'react-native-vector-icons/Ionicons';
import { THEME } from './index';

// Searchable modal dropdown — originally built inside AddProfileScreen, now
// shared so OnboardingScreen (and any future screen) doesn't reimplement it.
export const DP: React.FC<{
  value: string;
  options: readonly string[];
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  labelMap?: Record<string, string>;  // en → "English हिन्दी" combined label
  onSelect: (val: string) => void;
}> = ({ value, options, placeholder, disabled, loading, labelMap, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const getLabel = (v: string) => labelMap?.[v] ?? v;
  const filtered = options.filter(o => getLabel(o).toLowerCase().includes(search.toLowerCase()));

  return (
    <View>
      <TouchableOpacity
        style={[styles.dropdownBtn, !!value && styles.dropdownBtnActive, disabled && styles.dropdownBtnDisabled]}
        onPress={() => { if (!disabled && !loading) { setSearch(''); setOpen(true); } }}
        activeOpacity={0.8}>
        {loading
          ? <ActivityIndicator size="small" color={THEME.primary} style={{ marginRight: 8 }} />
          : null}
        <Text style={[styles.dropdownBtnText, !!value && styles.dropdownBtnTextActive]} numberOfLines={1}>
          {value ? getLabel(value) : (placeholder || 'Select...')}
        </Text>
        <Icon name="chevron-down" size={16} color={value ? THEME.primary : THEME.textLight} />
      </TouchableOpacity>

      <Modal visible={open} animationType="slide" transparent onRequestClose={() => { setOpen(false); setSearch(''); }}>
        <KeyboardAvoidingView
          style={styles.modalOverlay}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}>
          <SafeAreaView style={styles.modalSheet}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{placeholder ?? 'Select'}</Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                {!!value && (
                  <TouchableOpacity onPress={() => { onSelect(''); setOpen(false); setSearch(''); }} style={{ paddingVertical: 10, paddingHorizontal: 8 }}>
                    <Text style={{ fontSize: 13, color: THEME.primary, fontWeight: '600' }}>Clear</Text>
                  </TouchableOpacity>
                )}
                <TouchableOpacity onPress={() => { setOpen(false); setSearch(''); }} style={{ padding: 10 }}>
                  <Icon name="close" size={22} color={THEME.text} />
                </TouchableOpacity>
              </View>
            </View>
            <View style={styles.modalSearch}>
              <Icon name="search-outline" size={18} color={THEME.textLight} />
              <TextInput
                style={styles.modalSearchInput}
                value={search}
                onChangeText={setSearch}
                placeholder="Search..."
                placeholderTextColor={THEME.textLight}
                autoFocus
              />
              {search.length > 0 && (
                <TouchableOpacity onPress={() => setSearch('')}>
                  <Icon name="close-circle" size={18} color={THEME.textLight} />
                </TouchableOpacity>
              )}
            </View>
            <FlatList
              data={filtered}
              keyExtractor={item => item}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[styles.modalOption, value === item && styles.modalOptionActive]}
                  onPress={() => { onSelect(value === item ? '' : item); setOpen(false); setSearch(''); }}>
                  <Text style={[styles.modalOptionText, value === item && styles.modalOptionTextActive]}>
                    {getLabel(item)}
                  </Text>
                  {value === item && (
                    <Icon name="checkmark" size={18} color={THEME.primary} />
                  )}
                </TouchableOpacity>
              )}
              keyboardShouldPersistTaps="handled"
              contentContainerStyle={{ paddingBottom: 40 }}
              ListEmptyComponent={
                <View style={styles.modalEmpty}>
                  <Icon name="search-outline" size={32} color={THEME.border} />
                  <Text style={styles.modalEmptyText}>No results for "{search}"</Text>
                </View>
              }
            />
          </SafeAreaView>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  dropdownBtn: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 14, paddingVertical: 13, backgroundColor: THEME.card,
  },
  dropdownBtnActive: { borderColor: THEME.primary },
  dropdownBtnDisabled: { opacity: 0.5 },
  dropdownBtnText: { fontSize: 15, color: THEME.textLight, flex: 1 },
  dropdownBtnTextActive: { color: THEME.primary, fontWeight: '600' },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)', justifyContent: 'flex-end' },
  modalSheet: {
    backgroundColor: THEME.bg, borderTopLeftRadius: 20, borderTopRightRadius: 20,
    height: '75%',
  },
  modalHeader: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 20, paddingTop: 20, paddingBottom: 12,
    borderBottomWidth: 1, borderBottomColor: THEME.border,
  },
  modalTitle: { fontSize: 17, fontWeight: '800', color: THEME.text },
  modalSearch: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    marginHorizontal: 16, marginVertical: 12,
    borderWidth: 1.5, borderColor: THEME.border, borderRadius: 12,
    paddingHorizontal: 12, paddingVertical: 10, backgroundColor: THEME.card,
  },
  modalSearchInput: { flex: 1, fontSize: 15, color: THEME.text, padding: 0 },
  modalOption: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    paddingHorizontal: 20, paddingVertical: 14,
    borderBottomWidth: 1, borderBottomColor: THEME.border,
  },
  modalOptionActive: { backgroundColor: '#FAE5E5' },
  modalOptionText: { fontSize: 15, color: THEME.text },
  modalOptionTextActive: { color: THEME.primary, fontWeight: '700' },
  modalEmpty: { alignItems: 'center', justifyContent: 'center', paddingVertical: 48, gap: 12 },
  modalEmptyText: { fontSize: 14, color: THEME.textLight, textAlign: 'center' },
});
